/**
 * ============================================================================
 *  TESTIMONIALS — single source of truth for the homepage social proof
 * ============================================================================
 *
 * There are two exports:
 *   • FEATURED_TESTIMONIAL — the one big, photo-forward card (currently the
 *     Eclipse Paving / Justin Kirby review, linked to the case study).
 *   • TESTIMONIALS         — the array of additional review cards shown in the
 *     grid below the featured card.
 *
 * ── HOW TO ADD A NEW TESTIMONIAL ───────────────────────────────────────────
 * Copy the template object below, uncomment it, and fill every field. That's
 * the only edit you need — the homepage renders whatever is in this array and
 * automatically shows "add your review" placeholder slots when there's room.
 *
 *   {
 *     name: "Jane Smith",                 // reviewer's name
 *     role: "Owner",                      // their job title / role
 *     company: "Smith Joinery",           // their business name
 *     photo: "/path/to/photo.webp",       // headshot URL, or null for initials
 *     quote: "They transformed our...",   // the review text (no surrounding quotes)
 *     googleReviewUrl: "https://g.page/r/xxxx/review", // link to the Google review, or null
 *     caseStudyUrl: "/case-studies",      // OPTIONAL internal case-study link
 *   }
 *
 * IMPORTANT: only add REAL reviews. A smaller amount of genuine proof beats
 * padded proof — leave the array short (or empty) rather than inventing quotes.
 * ============================================================================
 */

export type Testimonial = {
  /** Reviewer's name. */
  name: string;
  /** Their job title / role, e.g. "Founder". */
  role: string;
  /** Their business / company name. */
  company: string;
  /** Headshot image URL, or `null` to render their initial instead. */
  photo: string | null;
  /** The review text (do not wrap in quote marks — the UI adds them). */
  quote: string;
  /** Link to this reviewer's Google review, or `null` if none. */
  googleReviewUrl: string | null;
  /** OPTIONAL internal link (e.g. a case study) that makes the whole card clickable. */
  caseStudyUrl?: string;
  /** OPTIONAL small muted disclosure line rendered beneath the card. */
  disclosure?: string;
};

/**
 * The featured (large) testimonial. Photo is shown prominently and the card
 * links to the Eclipse Paving case study.
 */
export const FEATURED_TESTIMONIAL: Testimonial = {
  name: "Justin Kirby",
  role: "Founder",
  company: "Eclipse Paving",
  photo: "/__l5e/assets-v1/e859d32c-744f-4f6a-9764-c1eb1ebac5e1/justin-kirby.jpeg",
  quote:
    "From zero online presence to a fully managed digital operation. Morgan and the team explained everything in plain English, built us a site we're proud of, and the leads have been coming in ever since.",
  googleReviewUrl: null,
  caseStudyUrl: "/case-studies",
};

/** Disclosure shown wherever the BowlScoreboard case study or quote appears. */
export const BOWLSCOREBOARD_DISCLOSURE =
  "Digitally Baffled runs BowlScoreboard's advertising sales and works alongside its leadership on commercial strategy — a partnership that grew out of this project.";

/**
 * Additional testimonials shown in the grid. ADD NEW ONES HERE (see the guide
 * at the top of this file).
 */
export const TESTIMONIALS: Testimonial[] = [
  {
    name: "Chris P Spry",
    role: "Founder & CEO",
    company: "BowlScoreboard",
    photo: null,
    quote:
      "We were an operational business making good sales when we sat down with Digitally Baffled. They rebuilt our brand and website — but the bigger thing was the idea they brought to the table: give clubs the board free, and fund it through local businesses sponsoring the advertising space. It changed how the whole company works. We ended up bringing them in to run our ad sales, and onto our commercial team. That says it all really.",
    googleReviewUrl: null,
    disclosure: BOWLSCOREBOARD_DISCLOSURE,
  },
];

